import { useCallback } from 'react';

import { useLazyGetEmissionEvidenceDownloadUrlQuery } from '../api/emissions';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';

const MISSING_URL_ERROR = new Error('Evidence not found — it may have been deleted.');

export type DownloadOutcome = { ok: true } | { ok: false; error: unknown };

interface DownloadOptions {
  /** Skip the per-call toast — useful for bulk callers that emit a summary. */
  silent?: boolean;
}

export const useDownloadEvidence = (initiativeId: string) => {
  const [fetchDownloadUrl] = useLazyGetEmissionEvidenceDownloadUrlQuery();

  const download = useCallback(
    async (evidenceId: string, options?: DownloadOptions): Promise<DownloadOutcome> => {
      let result: { url: string } | null;
      try {
        result = await fetchDownloadUrl({ initiativeId, evidenceId }).unwrap();
      } catch (error) {
        if (!options?.silent) {
          toastFromError({ error, title: 'Download failed' });
        }
        return { ok: false, error };
      }
      if (!result?.url) {
        if (!options?.silent) {
          generateToast({
            color: 'danger',
            title: 'Download failed',
            message: MISSING_URL_ERROR.message,
          });
        }
        return { ok: false, error: MISSING_URL_ERROR };
      }
      const anchor = document.createElement('a');
      anchor.href = result.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      try {
        anchor.click();
      } finally {
        document.body.removeChild(anchor);
      }
      return { ok: true };
    },
    [fetchDownloadUrl, initiativeId]
  );

  return { download };
};
